import { z } from 'zod';
import { normalizeHost } from './host.js';
import { MIN_BP, TOTAL_BP } from './weights.js';

/** Response envelope used by every endpoint. */
export type ApiError = { code: string; message: string; fields?: Record<string, string> };
export type ApiResponse<T> =
  | { success: true; data: T; error: null; meta?: Record<string, unknown> }
  | { success: false; data: null; error: ApiError };

export const hostnameSchema = z
  .string()
  .trim()
  .min(1, 'A hostname is required')
  .refine((value) => !value.includes('/'), { message: 'Enter a hostname only, without a path' })
  .refine((value) => !value.includes('*'), { message: 'Wildcard hostnames are not supported' })
  .transform((value) => normalizeHost(value))
  .refine((value) => value.length > 0, { message: 'A hostname is required' })
  .refine((value) => value.includes('.') || value.startsWith('['), {
    message: 'Enter a fully qualified hostname, for example client.example.com',
  })
  .refine((value) => /^[a-z0-9.-]+$|^\[[0-9a-f:]+\]$/.test(value), {
    message: 'Hostname contains characters that are not valid',
  });

export const entryPathSchema = z
  .string()
  .trim()
  .min(1, 'A path is required')
  .refine((value) => !/^[a-z][a-z0-9+.-]*:\/\//i.test(value), {
    message: 'Enter a path, not a full URL',
  })
  .refine((value) => !value.includes('?'), { message: 'Do not include a query string' })
  .refine((value) => !value.includes('#'), { message: 'Do not include a fragment' })
  .transform((value) => (value.startsWith('/') ? value : `/${value}`));

export const destinationUrlSchema = z
  .string()
  .trim()
  .min(1, 'A destination URL is required')
  .refine((value) => value.startsWith('http://') || value.startsWith('https://'), {
    message: 'Destination must start with http:// or https://',
  })
  .refine(
    (value) => {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'That is not a valid URL' },
  );

const optionalHttpUrl = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .refine(
    (value) => value === null || value.startsWith('http://') || value.startsWith('https://'),
    { message: 'Must start with http:// or https://' },
  );

export const loginRequest = z.object({
  email: z.string().trim().email().toLowerCase(),
  password: z.string().min(12, 'Password must be at least 12 characters'),
});

export const changePasswordRequest = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12, 'Password must be at least 12 characters'),
});

/**
 * An invitation asks for an email address and nothing else.
 *
 * Deliberately no password field: an administrator never sets somebody else's
 * password, so the only way an account gets one is the invitation link.
 */
export const inviteUserRequest = z.object({
  email: z.string().trim().email().toLowerCase(),
  role: z.enum(['admin', 'viewer']).default('admin'),
});

/** Claiming an invitation. The same 12-character floor as a password change. */
export const acceptInviteRequest = z.object({
  password: z.string().min(12, 'Password must be at least 12 characters'),
});

export const groupInput = z.object({
  name: z.string().trim().min(1, 'A name is required').max(120),
  notes: z.string().trim().max(2_000).default(''),
});

export const domainInput = z.object({
  hostname: hostnameSchema,
  fallbackUrl: optionalHttpUrl.default(null),
  enabled: z.boolean().default(true),
});

export const entryInput = z.object({
  label: z.string().trim().max(120).default(''),
  path: entryPathSchema,
  enabled: z.boolean().default(true),
});

/**
 * Updating an entry, which may also move it to a different hostname.
 *
 * `hostname` is optional and absent means "leave it where it is", so a caller
 * that only renames a path never has to know which domain it is on. When it IS
 * given and names a hostname that is not yet configured, `groupId` says which
 * group the new domain is created under; without one the entry's current group
 * is used, since that is the only answer that cannot move a URL between
 * clients by omission.
 */
export const entryUpdateInput = entryInput.extend({
  hostname: hostnameSchema.optional(),
  groupId: z.number().int().positive().optional(),
});

export const destinationInput = z.object({
  url: destinationUrlSchema,
  /* Zero is allowed: see MIN_BP. A parked destination is configured, kept and
     never chosen. The set as a whole must still total 100%. */
  weightBp: z.number().int().min(MIN_BP, 'Weight cannot be negative').max(TOTAL_BP),
  enabled: z.boolean().default(true),
});

export const destinationsSaveRequest = z
  .object({ destinations: z.array(destinationInput).min(1, 'Add at least one destination') })
  .refine(
    (value) => value.destinations.reduce((total, row) => total + row.weightBp, 0) === TOTAL_BP,
    { message: 'Weights must total exactly 100%', path: ['destinations'] },
  )
  .refine(
    (value) => new Set(value.destinations.map((row) => row.url)).size === value.destinations.length,
    { message: 'Each destination URL must be unique within an entry', path: ['destinations'] },
  );

export const settingsInput = z.object({
  globalFallbackUrl: optionalHttpUrl.default(null),
  minuteRetentionDays: z.number().int().min(1).max(90),
  hourRetentionDays: z.number().int().min(1).max(1_095),
  splitDeltaTolerancePct: z.number().min(0).max(50),
});

export const resolveRequest = z.object({
  url: z.string().trim().min(1, 'Enter a URL to test'),
  samples: z.number().int().min(0).max(10_000).default(1_000),
});

export const reportQuery = z
  .object({
    scope: z.enum(['group', 'domain', 'entry']),
    id: z.coerce.number().int().positive(),
    from: z.string().datetime(),
    to: z.string().datetime(),
    granularity: z.enum(['auto', 'minute', 'hour', 'day']).default('auto'),
  })
  .refine((value) => Date.parse(value.from) < Date.parse(value.to), {
    message: 'The start of the range must be before the end',
    path: ['from'],
  });

export type LoginRequest = z.infer<typeof loginRequest>;
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;
export type InviteUserRequest = z.infer<typeof inviteUserRequest>;
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequest>;
export type GroupInput = z.infer<typeof groupInput>;
export type DomainInput = z.infer<typeof domainInput>;
export type EntryInput = z.infer<typeof entryInput>;
export type EntryUpdateInput = z.infer<typeof entryUpdateInput>;
export type DestinationInput = z.infer<typeof destinationInput>;
export type DestinationsSaveRequest = z.infer<typeof destinationsSaveRequest>;
export type SettingsInput = z.infer<typeof settingsInput>;
export type ResolveRequest = z.infer<typeof resolveRequest>;
export type ReportQuery = z.infer<typeof reportQuery>;
